import { useLazyQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { gengql } from '@/__generated__';

import type { CandidateExpert } from './myProjects.types';

const ER_CANDIDATES = gengql(/* GraphQL */ `
  query dashboardERCandidates($erId: String!, $pageSize: Int!, $states: [String]) {
    expertRequestCandidates(id: $erId, first: $pageSize, states: $states) {
      edges {
        node {
          id
          state
          last_state_change
          match_experience
          profile {
            id
            first_name
            last_name
            name
            picture_url
            title
            html_url
          }
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`);

const VISIBLE_STATES = [
  'suggested_by_research',
  'suggested_by_platform',
  'vetting',
  'verified',
  'matched',
];

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useERCandidates() {
  const [execute, { data, loading, error }] = useLazyQuery(ER_CANDIDATES, {
    fetchPolicy: 'cache-and-network',
  });

  const fetchCandidates = useCallback(
    (erId: string) => {
      execute({ variables: { erId, pageSize: 10, states: VISIBLE_STATES } });
    },
    [execute]
  );

  const candidates = useMemo((): CandidateExpert[] => {
    const edges = data?.expertRequestCandidates?.edges ?? [];
    return edges
      .map((e) => e.node)
      .filter(Boolean)
      .map((node) => ({
        id: node.id,
        name:
          node.profile?.name ??
          `${node.profile?.first_name ?? ''} ${node.profile?.last_name ?? ''}`.trim(),
        firstName: node.profile?.first_name ?? '',
        lastName: node.profile?.last_name ?? '',
        pictureUrl: node.profile?.picture_url ?? null,
        title: node.profile?.title ?? null,
        htmlUrl: node.profile?.html_url ?? '',
        state: node.state ?? '',
        lastStateChange: node.last_state_change ?? null,
        matchExperience: node.match_experience ?? null,
      }));
  }, [data]);

  return { fetchCandidates, candidates, loading, error };
}
